import { Button, Text } from '$components'
import { XIcon, CompassIcon } from '$components'
import { cn } from '$components'
import { motion, AnimatePresence } from 'framer-motion'
import { useOnboardingTutorial } from '../hooks/use-onboarding-tutorial'
import type { OnboardingWizardConfig } from '../types'
import { useModalRegistry } from '../../../contexts/modal-registry'

export type OnboardingTutorialProps = {
  config: OnboardingWizardConfig
}

export function OnboardingTutorial({ config }: OnboardingTutorialProps) {
  const {
    currentStep,
    currentStepIndex,
    steps,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    toggleVisibility,
    isVisible,
    completedStepsCount,
    isTutorialCompleted,
    completeTutorialFlow,
    isCompletingTutorial,
  } = useOnboardingTutorial(config)

  const { isAnyModalOpen } = useModalRegistry()

  const hasStarted = currentStepIndex > 0 || completedStepsCount > 0

  // Don't render the tutorial if the user has already completed it
  if (isTutorialCompleted) {
    return null
  }

  return (
    <div className="relative">
      {/* Onboarding Indicator Button */}
      <button
        onClick={toggleVisibility}
        className={cn(
          'flex justify-center items-center rounded-2xl bg-neutral-100 border border-transparent hover:border-gray-200 p-3 min-w-12 min-h-12',
          {
            'border border-indigo-500 hover:border-indigo-600 bg-indigo-50': hasStarted,
          },
        )}
        aria-label={isVisible ? 'Collapse onboarding tutorial' : 'Expand onboarding tutorial'}
      >
        {hasStarted
          ? (
            <div className="flex flex-col items-center gap-1">
              <CompassIcon className="size-5 text-indigo-700" />

              <Text className="text-xs font-medium text-indigo-600">
                {currentStepIndex + 1}/{steps.length}
              </Text>
            </div>

          )
          : <CompassIcon className="size-5 text-gray-900" />
        }
      </button>

      {/* Expanded Popover */}
      <AnimatePresence>
        {!isAnyModalOpen && isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
            className="fixed left-4 bottom-4 w-[500px] z-20 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="p-4 pb-3">
              <div className="flex items-center justify-between">
                <Text
                  type="xs"
                  className="text-indigo-500 uppercase"
                >
                  Getting Started
                </Text>

                <div className="flex items-center gap-2">
                  <Text className="text-xs text-indigo-700">
                    Step {currentStepIndex + 1} of {steps.length}
                  </Text>

                  <button
                    type="button"
                    onClick={toggleVisibility}
                    className="p-2 border border-transparent hover:border-gray-100 hover:bg-white rounded-xl transition-colors"
                  >
                    <XIcon className="size-4 text-gray-700" />
                  </button>
                </div>
              </div>

              {/* Step indicators */}
              <div className="flex items-center gap-1 mt-3">
                {steps.map((step) => (
                  <div
                    key={step.id}
                    className={cn(
                      'flex-1 h-1.5 rounded-full transition-all duration-200',
                      {
                        'bg-green-500': step.isCompleted,
                        'bg-indigo-500': step.isActive && !step.isCompleted,
                        'bg-gray-200': !step.isActive && !step.isCompleted,
                      },
                    )}
                  />
                ))}
              </div>
            </div>

            {currentStep && (
              <div className="px-4 pb-4 flex flex-col gap-2">
                {/* Current step info */}
                <Text as="h3">
                  {currentStep.title}
                </Text>

                <Text type="s">
                  {currentStep.description}
                </Text>

                {/* Controls */}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button
                    variant="ghost"
                    onClick={previousStep}
                    disabled={isFirstStep}
                  >
                    Previous
                  </Button>

                  <Button
                    onClick={isLastStep ? completeTutorialFlow : nextStep}
                    isLoading={isLastStep && isCompletingTutorial}
                    disabled={isLastStep && isCompletingTutorial}
                  >
                    {isLastStep ? 'Complete' : 'Next'}
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
